import { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router';
import { Tabs } from '@/components/application/tabs/tabs';
import { LanguageSelector } from '@/components/application/language-selector/language-selector';
import { ThemeSwitcher } from '@/components/application/theme-switcher/theme-switcher';
import { ProfileAccountForm } from '@/components/application/profile/profile-account-form';
import { ProfilePasswordForm } from '@/components/application/profile/profile-password-form';
import { ProfilePictureUpload } from '@/components/application/profile/profile-picture-upload';
import { ProfileCompaniesList } from '@/components/application/profile/profile-companies-list';
import { EmailIntegrationTab } from '@/components/application/email';
import { TrackingIntegrationSection } from '@/components/application/tracking';
import { useTranslation } from 'react-i18next';

export const ProfilePage = () => {
    const { t } = useTranslation(['pages:profile', 'pages:legal', 'common']);
    const [searchParams, setSearchParams] = useSearchParams();
    const { pathname } = useLocation();
    const isAdminArea = pathname.startsWith('/admin');

    const validTabs = ['account', 'password', 'picture', 'companies', 'integrations', 'preferences'];
    const getValidTab = (tab: string | null): string => {
        return validTabs.includes(tab || '') ? tab! : 'account';
    };

    const [activeTab, setActiveTab] = useState<string>(
        getValidTab(searchParams.get('tab'))
    );

    // Leggere tab dall'URL all'inizializzazione
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam && validTabs.includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, [searchParams]);

    // Aggiornare URL quando cambia il tab
    const handleTabChange = (key: string) => {
        setActiveTab(key);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('tab', key);
        setSearchParams(newSearchParams, { replace: true });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('subtitle')}
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => handleTabChange(key as string)}>
                <Tabs.List items={[
                    { id: "account", label: t('tabs.account') },
                    { id: "password", label: t('tabs.password') },
                    { id: "picture", label: t('tabs.picture') },
                    { id: "companies", label: t('tabs.companies') },
                    { id: "integrations", label: t('tabs.integrations') },
                    { id: "preferences", label: t('tabs.preferences') },
                ]}>
                    <Tabs.Item id="account" label={t('tabs.account')} />
                    <Tabs.Item id="password" label={t('tabs.password')} />
                    <Tabs.Item id="picture" label={t('tabs.picture')} />
                    <Tabs.Item id="companies" label={t('tabs.companies')} />
                    <Tabs.Item id="integrations" label={t('tabs.integrations')} />
                    <Tabs.Item id="preferences" label={t('tabs.preferences')} />
                </Tabs.List>

                {/* Account Tab */}
                <Tabs.Panel id="account">
                    <div className="space-y-6">
                        <div className="rounded-lg border border-secondary bg-primary p-6">
                            <h3 className="text-lg font-semibold text-primary mb-1">
                                {t('account.title')}
                            </h3>
                            <p className="text-tertiary text-sm mb-6">
                                {t('account.description')}
                            </p>
                            <ProfileAccountForm />
                        </div>
                        {!isAdminArea && (
                            <div className="rounded-lg border border-secondary bg-primary p-6">
                                <h3 className="text-lg font-semibold text-primary mb-1">
                                    {t('account.legalSection.title')}
                                </h3>
                                <p className="text-tertiary text-sm mb-4">
                                    {t('account.legalSection.description')}
                                </p>
                                <div className="flex flex-wrap gap-x-4 gap-y-2">
                                    <a
                                        href="/terms"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-brand-600 hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                                    >
                                        {t('footer.terms', { ns: 'pages:legal' })}
                                    </a>
                                    <a
                                        href="/privacy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-brand-600 hover:text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                                    >
                                        {t('footer.privacy', { ns: 'pages:legal' })}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </Tabs.Panel>

                {/* Password Tab */}
                <Tabs.Panel id="password">
                    <div className="rounded-lg border border-secondary bg-primary p-6">
                        <h3 className="text-lg font-semibold text-primary mb-1">
                            {t('password.title')}
                        </h3>
                        <p className="text-tertiary text-sm mb-6">
                            {t('password.description')}
                        </p>
                        <ProfilePasswordForm />
                    </div>
                </Tabs.Panel>

                {/* Picture Tab */}
                <Tabs.Panel id="picture">
                    <div className="rounded-lg border border-secondary bg-primary p-6">
                        <h3 className="text-lg font-semibold text-primary mb-1">
                            {t('picture.title')}
                        </h3>
                        <p className="text-tertiary text-sm mb-6">
                            {t('picture.description')}
                        </p>
                        <ProfilePictureUpload />
                    </div>
                </Tabs.Panel>

                {/* Companies Tab */}
                <Tabs.Panel id="companies">
                    <div className="rounded-lg border border-secondary bg-primary p-6">
                        <h3 className="text-lg font-semibold text-primary mb-1">
                            {t('companies.title')}
                        </h3>
                        <p className="text-tertiary text-sm mb-6">
                            {t('companies.description')}
                        </p>
                        <ProfileCompaniesList />
                    </div>
                </Tabs.Panel>

                {/* Integrations Tab */}
                <Tabs.Panel id="integrations">
                    <div className="space-y-6">
                        {/* Email Integration */}
                        <div className="rounded-lg border border-secondary bg-primary p-6">
                            <EmailIntegrationTab />
                        </div>

                        {/* Tracking Integration */}
                        <div className="rounded-lg border border-secondary bg-primary p-6">
                            <TrackingIntegrationSection />
                        </div>
                    </div>
                </Tabs.Panel>

                {/* Preferences Tab */}
                <Tabs.Panel id="preferences">
                    <div className="space-y-6">
                        {/* Language Settings */}
                        <div className="rounded-lg border border-secondary bg-primary p-6">
                            <h3 className="text-lg font-semibold text-primary mb-1">
                                {t('preferences.language.title')}
                            </h3>
                            <p className="text-tertiary text-sm mb-4">
                                {t('preferences.language.description')}
                            </p>
                            <LanguageSelector />
                        </div>

                        {/* Theme Settings */}
                        <div className="rounded-lg border border-secondary bg-primary p-6">
                            <h3 className="text-lg font-semibold text-primary mb-1">
                                {t('preferences.theme.title')}
                            </h3>
                            <p className="text-tertiary text-sm mb-4">
                                {t('preferences.theme.description')}
                            </p>
                            <ThemeSwitcher />
                        </div>
                    </div>
                </Tabs.Panel>
            </Tabs>
        </div>
    );
};

